/**
 * Pure selector functions for dashboard data
 * 
 * These selectors take dashboard data as input (from useDashboardData)
 * and return transformed/computed values. They do NOT read from localStorage.
 * 
 * This makes them easy to test, reusable, and compatible with React Query.
 */

import { TimeWindow } from '../services/dashboardApi';
import { normalizeLogoUrl } from '../../../shared/utils/logoHelpers';
import { getWeekStartMonday, getWindowStartDate } from '../../../shared/utils/dateHelpers';
import { getPortfolioView, getAccountView } from '../../../shared/utils/chartToggles';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

// Match existing types from selectors.ts for compatibility, plus Redis data
export interface Allocation {
  name: string; // Card expects 'name' not 'title'
  value: number; // Percentage value for pie chart sizing (0-100)
  accountId: string;
  logoUrl?: string;
  pct?: number; // Same as value, kept for reference
}

export interface TopMover {
  symbol: string; // Card expects 'symbol' not 'ticker'
  name: string;
  change: string; // Card expects 'change' string for display (formatted)
  pct?: number; // Raw percentage value from Redis (for sorting/filtering)
}

export interface PortfolioSummary {
  pastDayPct: number;
  windowPct: number;
  windowDollar: number;
  totalValue?: number;
}

export interface AccountInfo {
  accountId: string;
  accountName: string;
  brokerage: string;
  logoUrl: string;
  currentValue: number | null;
  pct: number | null;
}

export interface ChartDataPoint {
  time: number;
  percent: number;
  dollar?: number;
}

export interface ChartData {
  series: ChartDataPoint[];
  headerPct: number | null;
  headerDollar: number | null;
  view: 'pct' | 'dollar'; // Required for compatibility with existing chart logic
}

export interface InvestmentTableRow {
  accountId: string;
  accountName?: string;
  logoUrl?: string;
  accountPct: number | null;
  isHidden?: boolean;
  hiddenTickers?: string[];
  isSnapTradeLinked?: boolean;
  tickers: TickerRow[];
}

// Match existing InvestmentTableSnapshot structure
export interface InvestmentTableSnapshot {
  accounts: InvestmentTableRow[];
}

export interface TickerRow {
  symbol: string;
  shares: number | null;
  myTotalPct: number | null | string; // Can be number, null, or "NA" for non-tickers with shares
  tickerPct: number | null;
  exchange: string | null; // For detecting non-tickers (exchange === 'MANUAL')
}

// Separate type for ticker price series (uses 'close' not 'dollar')
export interface TickerPricePoint {
  time: number;
  close: number;
}

export interface TickerChartData {
  priceSeries: TickerPricePoint[];
  name: string | null;
}

export interface TickerCostBasisPoint {
  time: number;
  basis: number | null;
  cumulativeDollarPnl?: number;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Convert time value to milliseconds
 * Handles both numeric timestamps and ISO date strings
 */
function toMs(t: any): number {
  if (typeof t === 'number') return t;
  const ms = Date.parse(t);
  return Number.isFinite(ms) ? ms : 0;
}

// ============================================================================
// PORTFOLIO SELECTORS
// ============================================================================

/**
 * Select account allocations (pie chart data)
 * 
 * Redis stores allocations and account info separately:
 * - allocationsAccount: { account_id, allocation_pct (new) or pct (old), current_account_value }
 * - accountInfo: { account_id, name, logo_url, ... }
 * 
 * We need to join them to get complete allocation data.
 */
export function selectAllocations(data: any): Allocation[] {
  if (!data?.portfolio?.summary?.allocationsAccount) return [];
  
  const allocations = data.portfolio.summary.allocationsAccount;
  const accountInfo = data.portfolio.summary.accountInfo || [];
  
  // Create a map of account_id -> account details for fast lookup
  const accountMap = new Map();
  accountInfo.forEach((acc: any) => {
    accountMap.set(acc.account_id || acc.accountId, {
      name: acc.name || acc.accountName || 'Unknown',
      logoUrl: acc.logo_url || acc.logoUrl,
    });
  });
  
  return allocations.map((acc: any) => {
    const accountId = acc.account_id || acc.accountId || '';
    const accountDetails = accountMap.get(accountId) || { name: 'Unknown', logoUrl: undefined };
    
    // Support both new format (allocation_pct) and old format (pct) for backward compatibility
    const allocationPct = acc.allocation_pct ?? acc.pct ?? 0;
    
    return {
      name: accountDetails.name,
      value: allocationPct, // Use percentage for pie chart
      accountId,
      logoUrl: normalizeLogoUrl(accountDetails.logoUrl),
      pct: allocationPct, // Also keep pct field for reference
    };
  });
}

/**
 * Select portfolio summary (header stats)
 */
export function selectPortfolioSummary(data: any, window: TimeWindow): PortfolioSummary {
  if (!data?.portfolio?.summary) {
    return { pastDayPct: 0, windowPct: 0, windowDollar: 0 };
  }
  
  const summary = data.portfolio.summary;
  const windows = summary.windows || {};
  const windowData = windows[window];
  
  return {
    pastDayPct: summary.pastDayPct ?? 0,
    windowPct: windowData?.pct ?? 0,
    windowDollar: windowData?.dollar ?? 0,
    totalValue: summary.currentValue,
  };
}

/**
 * Select account info list
 */
export function selectAccountInfo(data: any): AccountInfo[] {
  if (!data?.portfolio?.summary?.accountInfo) return [];
  
  return data.portfolio.summary.accountInfo.map((acc: any) => ({
    accountId: acc.accountId,
    accountName: acc.accountName || 'Unknown',
    brokerage: acc.brokerage || '',
    logoUrl: normalizeLogoUrl(acc.logoUrl),
    currentValue: acc.currentValue ?? null,
    pct: acc.pct ?? null,
  }));
}

/**
 * Select top movers
 * 
 * Redis stores top movers in a different structure:
 * - topMovers[window]: array of ticker symbols ["AAPL", "BTCUSD"]
 * - topMovers.tickers: object with ticker details { AAPL: { dayPctChange, company_name } }
 * 
 * We need to join these two to create the full mover objects.
 */
export function selectTopMovers(data: any, window: TimeWindow): TopMover[] {
  // Check for Redis structure (capital M)
  if (!data?.topMovers) return [];
  
  const topMovers = data.topMovers;
  // Structure: topMovers[window] = {tickers: [...]}
  const windowData = topMovers[window];
  const tickerSymbols = windowData?.tickers || []; // Array of symbols for this window
  const tickerDetails = topMovers.tickers || {}; // Object with ticker details
  
  if (!Array.isArray(tickerSymbols)) return [];
  
  // Join symbols with their details and prepare for sorting
  const movers: Array<TopMover & { _abs: number }> = [];
  
  for (const symbol of tickerSymbols) {
    const details = tickerDetails[symbol];
    if (!details) continue; // Skip if no details found
    
    const pct = details.dayPctChange ?? details.pct ?? 0;
    
    // Format as string with + or - prefix and 1 decimal place
    // Remove trailing .0 (e.g., +1.0% becomes +1%)
    let formattedPct = Math.abs(pct).toFixed(1);
    if (formattedPct.endsWith('.0')) {
      formattedPct = formattedPct.slice(0, -2);
    }
    const sign = pct >= 0 ? '+' : '-';
    const change = `${sign}${formattedPct}%`;
    
    movers.push({
      symbol,
      name: details.company_name || details.name || '',
      change,
      pct, // Preserve raw numeric value
      _abs: Math.abs(pct), // For sorting
    });
  }
  
  // Sort by absolute value of percentage change (largest to smallest)
  movers.sort((a, b) => b._abs - a._abs);
  
  // Remove the temporary _abs property and return
  return movers.map(({ _abs, ...mover }) => mover);
}

/**
 * Select portfolio chart data for given window
 * Note: Reads toggle state from localStorage to determine view (pct vs dollar)
 */
export function selectPortfolioChart(data: any, window: TimeWindow): ChartData {
  // Read current view from localStorage toggle (matches localStorage selector behavior)
  const view = getPortfolioView() === 'dollar' ? 'dollar' : 'pct';
  
  if (!data?.portfolio?.chart) {
    return { series: [], headerPct: null, headerDollar: null, view };
  }
  
  const chart = data.portfolio.chart;
  const windowData = chart[window];
  
  if (!windowData || !Array.isArray(windowData)) {
    return { series: [], headerPct: null, headerDollar: null, view };
  }
  
  // Map raw data to series format
  let series = windowData.map((point: any) => ({
    time: toMs(point.t ?? point.time ?? point.date),
    percent: point.pct ?? point.percent ?? 0,
    dollar: point.dollar,
  }));
  
  // For 1Y and 2Y, reduce to weekly buckets (Monday labels with latest values)
  if ((window === '1y' || window === '2y') && series.length > 0) {
    const weeklyBuckets = new Map<number, { time: number; percent: number; dollar?: number; latestTime: number }>();
    for (const pt of series) {
      const bucket = getWeekStartMonday(pt.time);
      const current = weeklyBuckets.get(bucket);
      if (!current || pt.time > current.latestTime) {
        weeklyBuckets.set(bucket, {
          time: bucket,
          percent: pt.percent,
          dollar: pt.dollar,
          latestTime: pt.time
        });
      }
    }
    series = Array.from(weeklyBuckets.values())
      .sort((a, b) => a.time - b.time)
      .map(b => ({ time: b.time, percent: b.percent, dollar: b.dollar }));
  }
  
  // Get header values from the last chart point (matches localStorage behavior)
  const headerPct = series.length > 0 ? series[series.length - 1]?.percent : null;
  const headerDollar = series.length > 0 ? series[series.length - 1]?.dollar : null;
  
  return {
    series,
    headerPct: typeof headerPct === 'number' ? headerPct : null,
    headerDollar: typeof headerDollar === 'number' ? headerDollar : null,
    view,
  };
}

/**
 * Select account chart data for given account and window
 * Note: Reads toggle state from localStorage to determine view (pct vs dollar)
 */
export function selectAccountChart(data: any, accountId: string, window: TimeWindow): ChartData {
  // Read current view from localStorage toggle (matches localStorage selector behavior)
  const view = getAccountView(accountId) === 'dollar' ? 'dollar' : 'pct';
  
  // Redis stores account charts at data.accounts[] (array), not data.portfolio.accounts
  if (!data?.accounts || !Array.isArray(data.accounts)) {
    return { series: [], headerPct: null, headerDollar: null, view };
  }
  
  // Find account by account_id (Redis uses account_id, not accountId)
  const account = data.accounts.find((acc: any) => acc.account_id === accountId);
  if (!account?.chart) {
    return { series: [], headerPct: null, headerDollar: null, view };
  }
  
  const windowData = account.chart[window];
  if (!windowData || !Array.isArray(windowData)) {
    return { series: [], headerPct: null, headerDollar: null, view };
  }
  
  // Map raw data to series format
  let series = windowData.map((point: any) => ({
    time: toMs(point.t ?? point.time ?? point.date),
    percent: point.pct ?? point.percent ?? 0,
    dollar: point.dollar,
  }));
  
  // For 1Y and 2Y, reduce to weekly buckets (Monday labels with latest values)
  if ((window === '1y' || window === '2y') && series.length > 0) {
    const weeklyBuckets = new Map<number, { time: number; percent: number; dollar?: number; latestTime: number }>();
    for (const pt of series) {
      const bucket = getWeekStartMonday(pt.time);
      const current = weeklyBuckets.get(bucket);
      if (!current || pt.time > current.latestTime) {
        weeklyBuckets.set(bucket, {
          time: bucket,
          percent: pt.percent,
          dollar: pt.dollar,
          latestTime: pt.time
        });
      }
    }
    series = Array.from(weeklyBuckets.values())
      .sort((a, b) => a.time - b.time)
      .map(b => ({ time: b.time, percent: b.percent, dollar: b.dollar }));
  }
  
  // Get header values from the last chart point (matches localStorage behavior)
  const headerPct = series.length > 0 ? series[series.length - 1]?.percent : null;
  const headerDollar = series.length > 0 ? series[series.length - 1]?.dollar : null;
  
  return {
    series,
    headerPct: typeof headerPct === 'number' ? headerPct : null,
    headerDollar: typeof headerDollar === 'number' ? headerDollar : null,
    view,
  };
}

/**
 * Select investment table data (accounts + tickers)
 * Named with plural "Investments" to match existing hook naming
 * Returns wrapped structure to match InvestmentTableSnapshot interface
 * 
 * Redis data structure:
 * - data.roster.accounts: { [accountId]: { company, logo_url, [window]: [symbols], is_hidden, hidden_tickers } }
 * - data.portfolio.summary.accountInfo: [{ account_id, name, ... }]
 * - data.accounts: [{ account_id, windows: {[w]: {pct}}, chart, ... }] (account performance data)
 * - data.tickerWindows: { [symbol]: { [window]: pct } }
 * - data.tickers: { [accountId]: { [symbol]: { shares, myTotalPct, chartCostBasis, ... } } }
 */
export function selectInvestmentsTable(data: any, window: TimeWindow): InvestmentTableSnapshot {
  if (!data?.roster?.accounts) {
    return { accounts: [] };
  }
  
  // Get account info for names
  const accountInfoArray = data.portfolio?.summary?.accountInfo || [];
  const accountInfoMap = new Map();
  accountInfoArray.forEach((acc: any) => {
    accountInfoMap.set(acc.account_id, acc);
  });
  
  // Get account performance data (windows with pct values)
  const accountsArray = data.accounts || [];
  const accountsDataMap = new Map();
  accountsArray.forEach((acc: any) => {
    accountsDataMap.set(acc.account_id, acc);
  });
  
  // Get global ticker performance
  const tickerWindows = data.tickerWindows || {};
  
  // Get ticker data per account (shares, myTotalPct, etc.)
  const tickersData = data.tickers || {};
  
  // Get ticker exchange info from topMovers (for non-ticker detection)
  const topMoversTickers = data.topMovers?.tickers || {};
  
  // Build accounts array
  const accounts: any[] = [];
  
  // Sort accounts by display_order before building the array
  const accountEntries = Object.entries(data.roster.accounts)
    .map(([accountId, accountData]: [string, any]) => ({
      accountId,
      accountData,
      displayOrder: accountData?.display_order ?? 999999, // Put accounts without order at the end
    }))
    .sort((a, b) => a.displayOrder - b.displayOrder);
  
  accountEntries.forEach(({ accountId, accountData }) => {
    // Get account metadata
    const accountInfo = accountInfoMap.get(accountId);
    const accountPerformanceData = accountsDataMap.get(accountId);
    const accountName = accountInfo?.name || accountData?.company || 'Unknown';
    
    // Get account performance from accounts array (data.accounts[].windows[window].pct)
    const accountPct = accountPerformanceData?.windows?.[window]?.pct ?? null;
    
    const logoUrl = normalizeLogoUrl(accountData?.logo_url || accountInfo?.logo_url);
    const isHidden = accountData?.is_hidden ?? false;
    const hiddenTickers = Array.isArray(accountData?.hidden_tickers) ? accountData.hidden_tickers : [];
    const isSnapTradeLinked = accountData?.is_snaptrade_linked === true;
    
    // Get tickers for this window
    const windowTickers: string[] = Array.isArray(accountData?.[window]) ? accountData[window] : [];
    
    // Get ticker details for this account
    const accountTickersData = tickersData[accountId] || {};
    
    // Build tickers array
    const tickers = windowTickers
      .map((symbol: string) => {
        const tickerData = accountTickersData[symbol] || {};
        const shares = tickerData.shares ?? null;
        const myTotalPct = tickerData.myTotalPct ?? null;
        const tickerPct = tickerWindows[symbol]?.[window] ?? null;
        // Get exchange info for non-ticker detection (exchange === 'MANUAL' means non-ticker)
        const exchange = topMoversTickers[symbol]?.exchange || null;
        
        return {
          symbol: symbol.toUpperCase(),
          shares,
          myTotalPct,
          tickerPct,
          exchange,
        };
      })
      .sort((a: any, b: any) => a.symbol.localeCompare(b.symbol));
    
    accounts.push({
      accountId,
      accountName,
      logoUrl,
      accountPct,
      isHidden,
      hiddenTickers,
      isSnapTradeLinked,
      tickers,
    });
  });
  
  return { accounts };
}

/**
 * Select ticker chart data
 * Returns price series with {time, close} shape for compatibility with existing chart logic
 * Redis stores ticker price history at data.tickerCharts[ticker].ticker_history
 */
export function selectTickerChart(
  data: any,
  accountId: string,
  ticker: string,
  window: TimeWindow
): TickerChartData {
  if (!ticker || !data?.tickerCharts) {
    return { priceSeries: [], name: null };
  }
  
  const tickerData = data.tickerCharts[ticker.toUpperCase()];
  if (!tickerData?.ticker_history || !Array.isArray(tickerData.ticker_history)) {
    return { priceSeries: [], name: ticker };
  }
  
  // Get company name from topMovers.tickers (same as localStorage version)
  // Fallback to ticker symbol if not found
  const tickerUpper = ticker.toUpperCase();
  const companyName = data.topMovers?.tickers?.[tickerUpper]?.company_name;
  const name = companyName || ticker;
  
  // Filter by time window
  const windowStart = getWindowStartDate(window).getTime();
  let priceSeries = tickerData.ticker_history
    .filter((point: any) => {
      const time = toMs(point.t ?? point.time);
      return time >= windowStart;
    })
    .map((point: any) => ({
      time: toMs(point.t ?? point.time),
      close: point.price ?? point.close ?? 0,
    }));
  
  // For 1Y and 2Y, reduce to weekly buckets (Monday labels with latest values)
  if ((window === '1y' || window === '2y') && priceSeries.length > 0) {
    const weeklyBuckets = new Map<number, { time: number; close: number; latestTime: number }>();
    for (const pt of priceSeries) {
      const bucket = getWeekStartMonday(pt.time);
      const current = weeklyBuckets.get(bucket);
      if (!current || pt.time > current.latestTime) {
        weeklyBuckets.set(bucket, {
          time: bucket,
          close: pt.close,
          latestTime: pt.time
        });
      }
    }
    priceSeries = Array.from(weeklyBuckets.values())
      .sort((a, b) => a.time - b.time)
      .map(b => ({ time: b.time, close: b.close }));
  }
  
  return {
    priceSeries,
    name,
  };
}

/**
 * Select ticker cost basis data
 */
/**
 * Select ticker cost basis data
 * Redis stores cost basis at data.tickers[accountId][ticker].chartCostBasis (flat array, not organized by window)
 * Returns TickerCostBasisPoint[] with { time, basis, cumulativeDollarPnl }
 */
export function selectTickerCostBasis(
  data: any,
  accountId: string,
  ticker: string,
  window: TimeWindow
): TickerCostBasisPoint[] {
  if (!accountId || !ticker || !data?.tickers) return [];
  
  const accountTickers = data.tickers[accountId];
  if (!accountTickers || typeof accountTickers !== 'object') return [];
  
  const tickerData = accountTickers[ticker.toUpperCase()];
  if (!tickerData?.chartCostBasis || !Array.isArray(tickerData.chartCostBasis)) {
    return [];
  }
  
  // Filter by time window (chartCostBasis is a flat array, not organized by window)
  const windowStart = getWindowStartDate(window).getTime();
  let series = tickerData.chartCostBasis
    .filter((point: any) => {
      const time = toMs(point.t ?? point.time ?? point.date);
      return time >= windowStart;
    })
    .map((point: any) => ({
      time: toMs(point.t ?? point.time ?? point.date),
      basis: point.costBasis !== undefined ? point.costBasis : null,
      cumulativeDollarPnl: point.cumulativeDollarPnl,
    }));
  
  // For 1Y and 2Y, reduce to weekly buckets (Monday labels with latest values)
  if ((window === '1y' || window === '2y') && series.length > 0) {
    const weeklyBuckets = new Map<number, { time: number; basis: number | null; cumulativeDollarPnl?: number; latestTime: number }>();
    for (const pt of series) {
      const bucket = getWeekStartMonday(pt.time);
      const current = weeklyBuckets.get(bucket);
      if (!current || pt.time > current.latestTime) {
        weeklyBuckets.set(bucket, {
          time: bucket,
          basis: pt.basis,
          cumulativeDollarPnl: pt.cumulativeDollarPnl,
          latestTime: pt.time
        });
      }
    }
    series = Array.from(weeklyBuckets.values())
      .sort((a, b) => a.time - b.time)
      .map(b => ({ time: b.time, basis: b.basis, cumulativeDollarPnl: b.cumulativeDollarPnl }));
  }
  
  return series;
}

/**
 * Select activity events (trading activity markers)
 * Redis stores events at data.portfolio.events (not data.act.events.portfolio)
 * Structure: {account_id: {ticker: events[]}}
 */
export function selectActivityEvents(data: any): any {
  if (!data?.portfolio?.events) return {};
  return data.portfolio.events;
}

/**
 * Select roster data (tickers by account)
 */
export function selectRoster(data: any): any {
  if (!data?.roster?.accounts) return { accounts: [] };
  return { accounts: data.roster.accounts };
}

/**
 * Select accounts for account management modal
 * Reads from Redis data structure:
 * - data.roster.accounts: { [accountId]: { company, logo_url, is_hidden, is_manually_edited, created_at, ... } }
 * - data.portfolio.summary.accountInfo: [{ account_id, name, current_value, current_value_updated_at, ... }]
 * 
 * Returns InvestmentAccount[] with all accounts (including hidden ones) for the modal.
 * Hidden accounts will be grayed out in the UI.
 */
export function selectAccounts(data: any, userId: string): any[] {
  if (!data?.roster?.accounts || !userId) return [];
  
  // Get account info for names and current values
  const accountInfoArray = data.portfolio?.summary?.accountInfo || [];
  const accountInfoMap = new Map();
  accountInfoArray.forEach((acc: any) => {
    accountInfoMap.set(acc.account_id, acc);
  });
  
  // Build accounts array from roster
  return Object.entries(data.roster.accounts).map(([accountId, accountData]: [string, any]) => {
    const accountInfo = accountInfoMap.get(accountId);
    return {
      id: accountId,
      accountName: accountInfo?.name ?? accountId,
      company: accountData?.company ?? '',
      logoUrl: normalizeLogoUrl(accountData?.logo_url || accountInfo?.logo_url),
      transactions: [], // Transactions loaded separately via fetchAndSetTransactionsForAccount
      createdAt: accountData?.created_at ?? '',
      isHidden: !!accountData?.is_hidden, // Read from Redis roster data
      isManuallyEdited: !!accountData?.is_manually_edited,
      isSnapTradeLinked: accountData?.is_snaptrade_linked === true,
      current_value: accountInfo?.current_value ?? null,
      current_value_updated_at: accountInfo?.current_value_updated_at ?? null,
      type: '',
      balance: 0,
      user_id: userId,
    };
  });
}
